import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Button from "../components/Button";
import { useLanguage } from "../contexts/LanguageContext";
import "../styles/Global.css";
import styles from "./PaymentCallback.module.css";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useAuthContext } from "../contexts/AuthContext";
import { trackGoogleAdsPurchase } from "../utils/googleAds";
import { trackTikTokCommerce } from "../utils/tiktok";
import { trackGa4 } from "../utils/analytics";
import { consumeCheckoutReturn } from "../utils/conversionJourney";

// Shape of the final result we show to the user
interface PaymentResult {
  id: string;
  status: string;
  message: string;
  flow?: "single" | "group";
}

// Backend base URL
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

/**
 * Payment Callback page component that handles the redirect after payment completion
 * @returns {JSX.Element} - Rendered payment callback page
 */
const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const hasRunRef = React.useRef(false);
  const { user } = useAuthContext();

  useEffect(() => {
    // Extract parameters from URL
    const payId = searchParams.get("id"); // Moyasar payment ID (optional)
    const statusParam = searchParams.get("status");
    const messageParam = searchParams.get("message") || "";
    const invoiceId = searchParams.get("invoice_id");
    const groupInvoiceId = searchParams.get("group_invoice_id");

    if (hasRunRef.current) return; // Prevent double execution under React 18 StrictMode

    if (groupInvoiceId) {
      verifyGroupInvoiceStatus(groupInvoiceId, statusParam);
      hasRunRef.current = true;
      return;
    }

    // If we have an invoice ID, trigger the backend callback verification
    if (invoiceId) {
      verifyWithBackendCallback(invoiceId, payId, statusParam, messageParam);
      hasRunRef.current = true;
    } else {
      // No invoice → go back to pricing
      navigate("/pricing");
    }
  }, [searchParams, navigate]);

  const verifyWithBackendCallback = async (
    invoiceId: string,
    payId: string | null,
    statusParam: string | null,
    messageParam: string | null
  ) => {
    try {
      const url = `${API_URL}/api/invoices/callback?id=${payId || ""
        }&invoice_id=${invoiceId}&json=1`;
      const response = await fetch(url);
      const data = await response.json();

      if (data && !data.error) {
        setPaymentResult({
          id: data.id || payId || invoiceId,
          status: data.status,
          message: data.message || messageParam || "",
          flow: "single",
        });

        // Show a single notification based on final status
        if (data.status === "paid") {
          trackTikTokCommerce("Purchase", invoiceId);
          trackGa4("purchase", {
            transaction_id: String(invoiceId),
            value: (data.amount_halala ?? data.amount ?? 0) / 100,
            currency: data.currency || "SAR",
          }, { persist: `purchase:${invoiceId}` });
          // Google Ads conversion is queued only after the backend has fetched
          // Moyasar and returned the authoritative paid invoice state.
          try {
            trackGoogleAdsPurchase({
              transactionId: invoiceId,
              value: (data.amount_halala ?? data.amount ?? 0) / 100,
              currency: data.currency || "SAR",
              email: user?.email,
            });
          } catch { }

          (window as any).showNotification?.(
            "Payment successful! Your credits have been added to your account.",
            "success"
          );
        } else {
          (window as any).showNotification?.(
            "Payment failed. Please try again or contact support.",
            "error"
          );
        }
      } else {
        // Backend returned an error – redirect user
        navigate("/pricing");
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
      (window as any).showNotification?.(
        "Error processing payment verification. Please contact support.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const verifyGroupInvoiceStatus = async (
    groupInvoiceId: string,
    statusParam: string | null
  ) => {
    if (statusParam === "back") {
      setPaymentResult({
        id: groupInvoiceId,
        status: "back",
        message: "Payment was not completed.",
        flow: "group",
      });
      setIsLoading(false);
      return;
    }

    try {
      let latest: any = null;

      for (let attempt = 0; attempt < 6; attempt += 1) {
        const res = await fetch(
          `${API_URL}/api/group-invoices/public/${groupInvoiceId}/status`
        );
        const data = await res.json();
        latest = data;

        if (!res.ok) {
          throw new Error(data.error || "Failed to load invoice status");
        }

        if (data.status !== "sent" && data.status !== "paid") {
          break;
        }

        if (attempt < 5) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      if (!latest) {
        throw new Error("No group invoice status returned");
      }

      setPaymentResult({
        id: groupInvoiceId,
        status: latest.status,
        message: latest.message || "",
        flow: "group",
      });

      if (latest.status === "activated") {
        (window as any).showNotification?.(
          "Payment successful. The group subscription has been activated.",
          "success"
        );
      } else if (latest.status === "activation_failed") {
        (window as any).showNotification?.(
          "Payment succeeded, but activation needs admin attention.",
          "warning"
        );
      } else if (latest.status === "canceled" || latest.status === "expired") {
        (window as any).showNotification?.(latest.message, "error");
      }
    } catch (error) {
      console.error("Error fetching group invoice status:", error);
      (window as any).showNotification?.(
        "Error processing group invoice status. Please contact support.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    if (paymentResult?.flow === "group") {
      navigate("/");
      return;
    }
    if (paymentResult?.status === "paid") {
      const invoiceId = searchParams.get("invoice_id");
      navigate(consumeCheckoutReturn(invoiceId) || "/chat");
    } else {
      navigate("/pricing");
    }
  };

  const handleContactSupport = () => {
    (window as any).showNotification?.(
      "Support contact feature coming soon. Please email support@c-shaped.com",
      "info"
    );
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <Header />
        <main className="main-content">
          <div style={{ textAlign: "center", padding: "50px" }}>
            <p>Processing payment result...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!paymentResult) {
    return (
      <div className="page-container">
        <Header />
        <main className="main-content">
          <div style={{ textAlign: "center", padding: "50px" }}>
            <p>No payment information found.</p>
            <Button onClick={() => navigate("/pricing")}>
              Return to Pricing
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isSuccess = ["paid", "activated", "activation_failed"].includes(paymentResult.status);
  const StatusIcon = isSuccess ? CheckCircleIcon : XCircleIcon;

  return (
    <div className="page-container">
      <Header />
      <main className="main-content">
        <div className={styles.callbackPage}>
          {/* Payment Status Card */}
          <div
            className={`${styles.statusCard} ${isSuccess ? styles.statusCardSuccess : styles.statusCardError
              }`}
          >
            {/* Status Icon */}
            <StatusIcon
              className={`${styles.statusIcon} ${isSuccess ? styles.statusIconSuccess : styles.statusIconError
                }`}
            />

            {/* Status Title */}
            <h1
              className={`${styles.statusTitle} ${isSuccess ? styles.statusTitleSuccess : styles.statusTitleError
                }`}
            >
              {isSuccess
                ? t.pricing.payment.callbackSuccess
                : t.pricing.payment.callbackFailed}
            </h1>

            {/* Status Message */}
            <p className={styles.statusMessage}>
              {isSuccess
                ? t.pricing.payment.successMessage
                : `${t.pricing.payment.failureMessage}: ${paymentResult.message}`}
            </p>

            {/* Payment Details */}
            <div className={styles.detailsBox}>
              <h3 className={styles.detailsTitle}>Payment Details:</h3>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>
                  {t.pricing.payment.paymentId}
                </span>{" "}
                <code className={styles.detailCode}>{paymentResult.id}</code>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>
                  {t.pricing.payment.status}
                </span>
                <span
                  className={
                    isSuccess
                      ? styles.detailStatusSuccess
                      : styles.detailStatusError
                  }
                >
                  {paymentResult.status.toUpperCase()}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>
                  {t.pricing.payment.message}
                </span>{" "}
                {paymentResult.message}
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <Button
                onClick={handleContinue}
                className={`${styles.primaryButton} ${isSuccess
                  ? styles.primaryButtonSuccess
                  : styles.primaryButtonError
                  }`}
              >
                {isSuccess
                  ? t.pricing.payment.startUsing
                  : t.pricing.payment.tryAgain}
              </Button>

              {!isSuccess && (
                <Button
                  onClick={handleContactSupport}
                  className={styles.secondaryButton}
                >
                  {t.pricing.payment.contactSupport}
                </Button>
              )}
            </div>

            {/* Additional Info for Failed Payments */}
            {!isSuccess && (
              <div className={styles.warningBox}>
                <h4 className={styles.warningTitle}>Common Issues:</h4>
                <ul className={styles.warningList}>
                  <li>Insufficient funds in your account</li>
                  <li>Card expired or blocked</li>
                  <li>Network connectivity issues</li>
                  <li>3D Secure authentication failed</li>
                </ul>
                <p className={styles.warningNote}>
                  If you continue to experience issues, please contact our
                  support team with your Payment ID for assistance.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentCallback;
